import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText,
  FormLabel, MenuItem, Radio, RadioGroup, Rating, Select, Stack, TextField, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageLayout, PageHeader, SectionCard, LoadingState } from '../../components/ui';
import { fetchMySurvey, saveProgress, submitSurvey, uploadSurveyFile } from '../../services/surveysApi';
import { resolveVisibility } from './visibility';

export default function SurveyRunnerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    fetchMySurvey(id)
      .then((res) => {
        if (!active) return;
        setData(res);
        setAnswers(res.my_answers && typeof res.my_answers === 'object' ? { ...res.my_answers } : {});
      })
      .catch(() => toast.error('This survey is not available.'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  const questions = useMemo(() => data?.questions || [], [data]);
  const visible = useMemo(() => resolveVisibility(questions, answers), [questions, answers]);

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[qid];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveProgress(id, answers);
      toast.success('Progress saved.');
    } catch {
      toast.error('Could not save progress.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      await submitSurvey(id, answers);
      toast.success('Thank you — your response has been submitted.');
      navigate('/my/surveys');
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data?.errors || {});
        toast.error(err.response.data?.message || 'Please review the highlighted questions.');
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState />;
  if (!data) return null;

  const visibleQuestions = questions.filter((q) => visible[q.id]);

  return (
    <PageLayout maxWidth="md">
      <PageHeader title={data.survey.title} subtitle={data.survey.description} />
      {data.survey.is_anonymous && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          🔒 Your responses to this survey are confidential.
        </Typography>
      )}

      <Stack spacing={2}>
        {visibleQuestions.map((q, idx) => (
          <SectionCard key={q.id}>
            <QuestionInput
              index={idx + 1}
              question={q}
              surveyId={id}
              value={answers[q.id]}
              error={Array.isArray(errors[q.id]) ? errors[q.id][0] : undefined}
              onChange={(v) => setAnswer(q.id, v)}
            />
          </SectionCard>
        ))}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={handleSave} disabled={saving || submitting}>
          {saving ? 'Saving…' : 'Save progress'}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </Stack>
    </PageLayout>
  );
}

function QuestionInput({ index, question, surveyId, value, error, onChange }) {
  const { type, label, help_text, is_required, options = [], settings = {} } = question;
  const heading = `${index}. ${label}${is_required ? ' *' : ''}`;
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const meta = await uploadSurveyFile(surveyId, file);
      onChange(meta);
    } catch {
      toast.error('Upload failed. Please try a smaller or supported file.');
    } finally {
      setUploading(false);
    }
  };

  if (type === 'single_choice' || type === 'yes_no') {
    const opts = type === 'yes_no' && options.length === 0
      ? [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]
      : options;
    return (
      <FormControl component="fieldset" error={!!error} fullWidth>
        <FormLabel sx={{ fontWeight: 600 }}>{heading}</FormLabel>
        {help_text && <FormHelperText sx={{ mx: 0 }}>{help_text}</FormHelperText>}
        <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          {opts.map((o) => (
            <FormControlLabel key={o.value} value={String(o.value)} control={<Radio />} label={o.label || o.value} />
          ))}
        </RadioGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'dropdown') {
    return (
      <FormControl error={!!error} fullWidth>
        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>{heading}</FormLabel>
        {help_text && <FormHelperText sx={{ mx: 0, mb: 1 }}>{help_text}</FormHelperText>}
        <Select value={value ?? ''} displayEmpty onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=""><em>Select…</em></MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={String(o.value)}>{o.label || o.value}</MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'multi_choice') {
    const selected = Array.isArray(value) ? value.map(String) : [];
    const toggle = (optValue) => {
      const v = String(optValue);
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
      onChange(next);
    };
    return (
      <FormControl component="fieldset" error={!!error} fullWidth>
        <FormLabel sx={{ fontWeight: 600 }}>{heading}</FormLabel>
        {help_text && <FormHelperText sx={{ mx: 0 }}>{help_text}</FormHelperText>}
        <FormGroup>
          {options.map((o) => (
            <FormControlLabel
              key={o.value}
              control={<Checkbox checked={selected.includes(String(o.value))} onChange={() => toggle(o.value)} />}
              label={o.label || o.value}
            />
          ))}
        </FormGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'rating') {
    const max = Number(settings.max ?? 5);
    return (
      <FormControl error={!!error} fullWidth>
        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>{heading}</FormLabel>
        {help_text && <FormHelperText sx={{ mx: 0, mb: 1 }}>{help_text}</FormHelperText>}
        <Rating
          max={max}
          value={Number(value) || 0}
          onChange={(_e, v) => onChange(v || 0)}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'date') {
    return (
      <TextField
        type="date"
        label={heading}
        helperText={error || help_text}
        error={!!error}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
    );
  }

  if (type === 'file') {
    const meta = value && typeof value === 'object' ? value : null;
    return (
      <FormControl error={!!error} fullWidth>
        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>{heading}</FormLabel>
        {help_text && <FormHelperText sx={{ mx: 0, mb: 1 }}>{help_text}</FormHelperText>}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" component="label" disabled={uploading}>
            {uploading ? 'Uploading…' : meta ? 'Replace file' : 'Upload file'}
            <input hidden type="file" onChange={handleFile} />
          </Button>
          {meta && <Typography variant="body2" color="text.secondary">{meta.original_name}</Typography>}
        </Stack>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  // text, textarea
  return (
    <TextField
      label={heading}
      helperText={error || help_text}
      error={!!error}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      multiline={type === 'textarea'}
      minRows={type === 'textarea' ? 3 : undefined}
      fullWidth
    />
  );
}
